/*
 * Video feed — Instagram / YouTube-Shorts style behaviour for the "videos" blog.
 *
 * Two responsive modes, decided by the same media query used for layout in CSS:
 *   - Touch / narrow (default): single-column feed. Each video autoplays (muted)
 *     when it scrolls into view and pauses when it leaves. After the first user
 *     gesture, the in-view video unmutes and sound carries to the next on scroll.
 *   - Desktop with a mouse ((min-width:750px) and (hover:hover) and (pointer:fine)):
 *     a multi-column grid of poster thumbnails. A video plays on mouse-over (like
 *     YouTube hover-preview) and pauses + resets when the mouse leaves.
 *
 * Infinite scroll: pagination is hidden and the next page is fetched + appended as
 * the visitor nears the bottom (data-next-url chains page to page).
 *
 * For every native <video> the script normalises it to muted + loop + playsinline,
 * strips the native controls, wraps it in a .video-feed__media overlay (mute toggle
 * + play affordance) and hoists it to the top of its card (video-first). Honours
 * prefers-reduced-motion: no autoplay/hover, native controls restored instead.
 */
(function () {
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Desktop-with-mouse → grid + hover-to-play. Touch / narrow → scroll feed.
  var HOVER_MQ = window.matchMedia('(min-width: 750px) and (hover: hover) and (pointer: fine)');

  var ICON_MUTED =
    '<svg class="video-feed__icon--off" viewBox="0 0 24 24" aria-hidden="true"><path d="M3.6 5 5 3.6l15.4 15.4L19 20.4l-3.3-3.3a6 6 0 0 1-1.7.8v-2.1l.2-.1L12 13.4V19l-5-4H3V9h2.6L3.6 5ZM12 5v4.2L8.8 6 12 5Z"/></svg>' +
    '<svg class="video-feed__icon--on" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5 7 9H3v6h4l5 4V5Zm4.5 3a5 5 0 0 1 0 8l-1.1-1.1a3.5 3.5 0 0 0 0-5.8L16.5 8Zm2 9.5a8 8 0 0 0 0-11l-1.1 1.1a6.5 6.5 0 0 1 0 8.8l1.1 1.1Z"/></svg>';

  var ICON_PLAY =
    '<span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg></span>';

  function setPaused(media, paused) {
    media.classList.toggle('is-paused', paused);
  }

  // Play a video; if an UNMUTED play is blocked by the browser, fall back to
  // muted playback so the video still runs (never stuck on the poster).
  function playVideo(v) {
    var p = v.play();
    if (p && p.catch) {
      p.catch(function () {
        if (!v.muted) {
          v.muted = true;
          var p2 = v.play();
          if (p2 && p2.catch) p2.catch(function () {});
        }
      });
    }
  }

  function buildPost(video, ctx) {
    if (video.dataset.vfReady) return null;
    video.dataset.vfReady = '1';

    video.loop = true;
    video.muted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('preload', 'metadata');

    var media = document.createElement('div');
    media.className = 'video-feed__media is-paused';

    var host = video.parentNode;
    var hostIsLoneP =
      host.tagName === 'P' &&
      host.childElementCount === 1 &&
      host.textContent.trim() === '';

    if (hostIsLoneP) {
      host.parentNode.insertBefore(media, host);
      media.appendChild(video);
      host.parentNode.removeChild(host);
    } else {
      host.insertBefore(media, video);
      media.appendChild(video);
    }

    // Hoist the video to the top of its card so each post reads video-first,
    // then text (title, caption, CTA) — the video sits mid body_html otherwise.
    var post = media.closest('.video-feed__post');
    if (post) post.insertBefore(media, post.firstChild);

    if (REDUCED) {
      // Accessibility: don't autoplay/hover, give the user native controls instead.
      video.controls = true;
      return null;
    }

    video.removeAttribute('controls');
    video.controls = false;

    // Play affordance overlay (hidden in the desktop grid via CSS).
    var playIcon = document.createElement('span');
    playIcon.className = 'video-feed__playicon';
    playIcon.setAttribute('aria-hidden', 'true');
    playIcon.innerHTML = ICON_PLAY;
    media.appendChild(playIcon);

    // Mute / unmute button.
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'video-feed__mute is-muted';
    btn.setAttribute('aria-label', 'Ativar som');
    btn.setAttribute('aria-pressed', 'false');
    btn.innerHTML = ICON_MUTED;
    media.appendChild(btn);

    btn.addEventListener('click', function (event) {
      event.stopPropagation();
      if (video.muted) {
        ctx.soundOn = true;
        ctx.current = video;
        ctx.muteOthers(video);
        video.muted = false;
        playVideo(video);
      } else {
        ctx.soundOn = false;
        video.muted = true;
      }
      ctx.syncMuteUI();
    });

    media.addEventListener('click', function (event) {
      if (event.target.closest('.video-feed__mute')) return;
      if (video.paused) {
        playVideo(video);
      } else {
        video.pause();
      }
    });

    // Desktop hover-preview: play on mouse-over, pause + reset on mouse-out.
    media.addEventListener('mouseenter', function () {
      if (!HOVER_MQ.matches) return;
      ctx.activate(video);
    });
    media.addEventListener('mouseleave', function () {
      if (!HOVER_MQ.matches) return;
      ctx.deactivate(video, true);
    });

    video.addEventListener('play', function () {
      setPaused(media, false);
    });
    video.addEventListener('pause', function () {
      setPaused(media, true);
    });

    return video;
  }

  // Fetch successive pages and append their posts as the visitor nears the bottom.
  // Uses a scroll listener + the inner container's measured bottom (reliable) rather
  // than an IntersectionObserver on a 1px sentinel, which proved flaky in production.
  function setupInfinite(root, ingest) {
    var inner = root.querySelector('.video-feed__inner');
    var nextUrl = root.getAttribute('data-next-url') || '';
    if (!inner || !nextUrl) return;

    var pagination = root.querySelector('.video-feed__pagination');
    if (pagination) pagination.style.display = 'none'; // JS takes over from page links

    var spinner = document.createElement('div');
    spinner.className = 'video-feed__sentinel';
    inner.insertAdjacentElement('afterend', spinner);

    var loading = false;
    var done = false;
    var scheduled = false;

    function onScroll() {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(check);
    }

    function check() {
      scheduled = false;
      if (loading || done || !nextUrl) return;
      // Within 1000px of the end of the posts grid? then pull the next page.
      if (inner.getBoundingClientRect().bottom > window.innerHeight + 1000) return;
      load();
    }

    function stop() {
      done = true;
      if (spinner.parentNode) spinner.remove();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    }

    function load() {
      loading = true;
      spinner.classList.add('is-loading');
      fetch(nextUrl)
        .then(function (r) { return r.text(); })
        .then(function (htmlText) {
          var doc = new DOMParser().parseFromString(htmlText, 'text/html');
          var posts = doc.querySelectorAll('.video-feed__inner .video-feed__post');
          posts.forEach(function (p) {
            inner.appendChild(document.importNode(p, true));
          });
          ingest(inner);
          var nextRoot = doc.querySelector('.video-feed');
          nextUrl = nextRoot ? nextRoot.getAttribute('data-next-url') || '' : '';
          loading = false;
          spinner.classList.remove('is-loading');
          if (!nextUrl) {
            stop();
          } else {
            onScroll(); // eager-fill if the new end is still near the viewport
          }
        })
        .catch(function () {
          loading = false;
          spinner.classList.remove('is-loading');
        });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll(); // initial check (short pages fill immediately)
  }

  function init(root) {
    if (root.dataset.vfInit) return;
    root.dataset.vfInit = '1';

    var autoplay = root.getAttribute('data-autoplay') !== 'false';
    if (!root.querySelector('.video-feed video')) return;

    var videos = [];
    var ctx = {
      soundOn: root.getAttribute('data-sound') === 'true',
      current: null,
      gesture: false,
      muteOthers: function (except) {
        videos.forEach(function (v) {
          if (v !== except) v.muted = true;
        });
      },
      // Has the visitor interacted yet? (required before audio can play)
      canSound: function () {
        if (navigator.userActivation) return navigator.userActivation.hasBeenActive;
        return ctx.gesture;
      },
      // Make a video the active one: apply sound policy and play it.
      activate: function (v) {
        ctx.current = v;
        if (ctx.soundOn && ctx.canSound()) {
          ctx.muteOthers(v);
          v.muted = false;
        } else {
          v.muted = true;
        }
        ctx.syncMuteUI();
        playVideo(v);
      },
      // Pause a video; reset to its poster when leaving (hover-preview).
      deactivate: function (v, reset) {
        v.pause();
        if (reset) {
          try {
            v.currentTime = 0;
          } catch (e) {}
        }
        v.muted = true;
        if (ctx.current === v) ctx.current = null;
        ctx.syncMuteUI();
      },
      syncMuteUI: function () {
        root.querySelectorAll('.video-feed__mute').forEach(function (button) {
          var media = button.closest('.video-feed__media');
          var v = media && media.querySelector('video');
          if (!v) return;
          button.classList.toggle('is-muted', v.muted);
          button.setAttribute('aria-pressed', String(!v.muted));
          button.setAttribute('aria-label', v.muted ? 'Ativar som' : 'Desativar som');
        });
      },
    };

    var canObserve = autoplay && !REDUCED && 'IntersectionObserver' in window;
    var io = null;
    if (canObserve) {
      io = new IntersectionObserver(
        function (entries) {
          if (HOVER_MQ.matches) return; // grid mode plays on hover, not on scroll
          entries.forEach(function (entry) {
            var v = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.55) {
              ctx.activate(v);
            } else {
              ctx.deactivate(v, false);
            }
          });
        },
        { threshold: [0, 0.55, 0.9] }
      );
    }

    // Process any not-yet-wired videos within a scope (initial + appended pages).
    function ingest(scope) {
      var fresh = Array.prototype.slice
        .call(scope.querySelectorAll('.video-feed video'))
        .filter(function (v) { return !v.dataset.vfReady; });
      fresh.forEach(function (v) {
        var ok = buildPost(v, ctx);
        videos.push(v);
        if (ok && io) io.observe(v);
      });
    }

    ingest(root);
    ctx.syncMuteUI();

    // First real user gesture unlocks audio: unmute whatever is currently active.
    function onFirstGesture() {
      ctx.gesture = true;
      if (ctx.soundOn && ctx.current && ctx.current.muted) {
        ctx.muteOthers(ctx.current);
        ctx.current.muted = false;
        playVideo(ctx.current);
        ctx.syncMuteUI();
      }
      window.removeEventListener('pointerdown', onFirstGesture, true);
      window.removeEventListener('touchstart', onFirstGesture, true);
      window.removeEventListener('keydown', onFirstGesture, true);
    }
    if (ctx.soundOn && !ctx.canSound()) {
      window.addEventListener('pointerdown', onFirstGesture, true);
      window.addEventListener('touchstart', onFirstGesture, true);
      window.addEventListener('keydown', onFirstGesture, true);
    }

    setupInfinite(root, ingest);
  }

  function boot() {
    document.querySelectorAll('.video-feed').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when re-rendered inside the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('.video-feed').forEach(init);
  });
})();
